// FILE: apps/admin/src/pages/Sessions.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Core business logic and definitions for Sessions.
//   SCOPE: Sessions operations, models, and helper routines.
//   DEPENDS: none
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   Sessions - Exported Sessions definition
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api.js';
import { AdminIcon } from '../components/Icons.js';
import { useToast } from '../toast.js';

interface SessionsData {
  tbank: { status: string; url: string };
  fragment: { ready: boolean };
}

const VP = { width: 1280, height: 900 };

// START_CONTRACT: Sessions
//   PURPOSE: Execute Sessions operation.
//   INPUTS: Defined in function signature
//   OUTPUTS: Return value matching signature
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: Sessions
export function Sessions() {
  // START_BLOCK_SESSIONS
  const qc = useQueryClient();
  const toast = useToast();
  const [shot, setShot] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [text, setText] = useState('');
  const imgRef = useRef<HTMLImageElement>(null);

  const { data } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<SessionsData>('/admin/sessions'),
    refetchInterval: 8000,
  });

  const refreshShot = useCallback(async () => {
    try {
      const r = await api.get<{ png: string }>('/admin/sessions/tbank/screenshot');
      setShot(`data:image/png;base64,${r.png}`);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!live) return;
    void refreshShot();
    const id = setInterval(() => void refreshShot(), 1600);
    return () => clearInterval(id);
  }, [live, refreshShot]);

  const gotoLogin = useMutation({
    mutationFn: () => api.post<{ url: string; blocked: boolean }>('/admin/sessions/tbank/goto-login'),
    onSuccess: (r) => {
      toast(r.blocked ? 'Т-Банк показывает блокировку — подождите' : 'Открыта страница входа');
      setLive(true);
      setTimeout(() => void refreshShot(), 900);
    },
    onError: () => toast('Ошибка'),
  });

  const onImgClick = async (e: MouseEvent<HTMLImageElement>) => {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * VP.width);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * VP.height);
    try {
      await api.post('/admin/sessions/tbank/click', { x, y });
    } catch {
      /* ignore */
    }
    setTimeout(() => void refreshShot(), 400);
  };

  const typeText = useMutation({
    mutationFn: () => api.post('/admin/sessions/tbank/type', { text }),
    onSuccess: () => {
      setText('');
      setTimeout(() => void refreshShot(), 400);
    },
    onError: () => toast('Ошибка'),
  });
  const key = useMutation({
    mutationFn: (k: string) => api.post('/admin/sessions/tbank/key', { key: k }),
    onSuccess: () => setTimeout(() => void refreshShot(), 400),
    onError: () => toast('Ошибка'),
  });
  const confirm = useMutation({
    mutationFn: () => api.post<{ status: string }>('/admin/sessions/tbank/confirm'),
    onSuccess: (r) => {
      toast(r.status === 'HEALTHY' ? 'Вход выполнен — HEALTHY' : `Статус: ${r.status}`);
      void qc.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: () => toast('Ошибка'),
  });
  const relogin = useMutation({
    mutationFn: () => api.post<{ status: string }>('/admin/sessions/tbank/relogin'),
    onSuccess: (r) => {
      toast(`Статус: ${r.status}`);
      void qc.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: () => toast('Ошибка'),
  });
  const fragmentReload = useMutation({
    mutationFn: () => api.post<{ ready: boolean }>('/admin/sessions/fragment/reload'),
    onSuccess: (r) => {
      toast(r.ready ? 'Fragment перечитан (HEALTHY)' : 'Fragment не готов');
      void qc.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: () => toast('Ошибка'),
  });

  return (
    <>
      <h1>Сессии</h1>
      <div className="resp-grid-2" style={{ gap: 14 }}>
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0 }}>T-Bank</h2>
            <span className={'chip ' + (data?.tbank.status ?? 'UNKNOWN')}>{data?.tbank.status ?? '…'}</span>
          </div>
          <div className="muted mono" style={{ wordBreak: 'break-all', margin: '10px 0' }}>{data?.tbank.url}</div>
          <button className="btn ghost sm" disabled={relogin.isPending} onClick={() => relogin.mutate()}>
            Релогин по cookies
          </button>
        </div>

        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0 }}>Fragment</h2>
            <span className={'chip ' + (data?.fragment.ready ? 'HEALTHY' : 'UNKNOWN')}>
              {data?.fragment.ready ? 'HEALTHY' : 'UNKNOWN'}
            </span>
          </div>
          <p className="muted">Сессия Fragment для выдачи звёзд/Premium.</p>
          <button className="btn sm" disabled={fragmentReload.isPending} onClick={() => fragmentReload.mutate()}>
            Перечитать сессию
          </button>
        </div>
      </div>

      <h2>Ручной вход в Т-Банк</h2>
      <p className="muted" style={{ marginTop: -8 }}>
        Входишь сам, как в обычном браузере: открой страницу входа, <b>кликай прямо по картинке</b>,
        печатай телефон/код в поле ниже. Сессия остаётся в нашем браузере. После входа нажми «Проверить вход».
      </p>
      <div className="card">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <button className="btn sm" disabled={gotoLogin.isPending} onClick={() => gotoLogin.mutate()}>
            Открыть страницу входа
          </button>
          <button className="btn ghost sm" onClick={() => void refreshShot()}>
            Обновить экран
          </button>
          <button type="button" className="btn ghost sm" onClick={() => setLive((v) => !v)}>
            <AdminIcon name={live ? 'pause' : 'play'} size={12} />
            <span>Авто-обновление</span>
          </button>
          <button type="button" className="btn sm" disabled={confirm.isPending} onClick={() => confirm.mutate()}>
            Проверить вход
          </button>
        </div>

        <div className="row" style={{ marginBottom: 12 }}>
          <input
            placeholder="текст (телефон / код)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            style={{ flex: '1 1 180px', minWidth: 0 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') typeText.mutate();
            }}
          />
          <button type="button" className="btn sm" disabled={!text || typeText.isPending} onClick={() => typeText.mutate()}>
            Напечатать
          </button>
          <button type="button" className="btn ghost sm" onClick={() => key.mutate('Enter')}>
            Enter
          </button>
          <button type="button" className="btn ghost sm" onClick={() => key.mutate('Backspace')}>
            Backspace
          </button>
        </div>

        {shot ? (
          <img
            ref={imgRef}
            src={shot}
            alt="tbank"
            onClick={onImgClick}
            style={{
              width: '100%',
              display: 'block',
              borderRadius: 10,
              border: '1px solid var(--hair)',
              cursor: 'crosshair',
            }}
          />
        ) : (
          <div className="muted" style={{ padding: 20, textAlign: 'center' }}>
            Нажми «Открыть страницу входа» или «Обновить экран», чтобы увидеть браузер.
          </div>
        )}
      </div>
    </>
  );

  // END_BLOCK_SESSIONS
}
